import { useState, useEffect, useCallback } from 'react'
import api from '../services/api'
import { useToast } from '../contexts/ToastContext'

export interface SocialInteractionState {
  liked: boolean
  saved: boolean
  reposted: boolean
  likesCount: number
  commentsCount: number
  repostsCount: number
  loading: {
    like: boolean
    save: boolean
    repost: boolean
  }
}

export const useSocialInteractions = (postId?: string, userId?: string) => {
  const { showToast } = useToast()
  const [state, setState] = useState<SocialInteractionState>({
    liked: false,
    saved: false,
    reposted: false,
    likesCount: 0,
    commentsCount: 0,
    repostsCount: 0,
    loading: {
      like: false,
      save: false,
      repost: false,
    },
  })

  // Load initial state from cache and API
  useEffect(() => {
    if (postId) {
      loadPostInteractionState()
    }
  }, [postId])

  const loadPostInteractionState = async () => {
    try {
      // Load from local cache for instant UI
      const liked = api.isPostLiked(postId!)
      const saved = api.isPostBookmarked(postId!)
      const reposted = api.isPostReposted(postId!)

      setState(prev => ({
        ...prev,
        liked,
        saved,
        reposted,
      }))
    } catch (error) {
      console.error('Failed to load post interaction state:', error)
    }
  }

  // Like/Unlike functionality
  const toggleLike = useCallback(async () => {
    if (!postId || state.loading.like) return

    setState(prev => ({
      ...prev,
      loading: { ...prev.loading, like: true },
    }))

    const newLiked = !state.liked
    const newCount = newLiked ? state.likesCount + 1 : state.likesCount - 1

    // Optimistic update
    setState(prev => ({
      ...prev,
      liked: newLiked,
      likesCount: Math.max(0, newCount),
    }))

    try {
      const response = newLiked
        ? await api.likePost(postId)
        : await api.unlikePost(postId)

      if (response.success) {
        // Update cache
        const cacheData = {
          liked: newLiked,
          saved: state.saved,
          reposted: state.reposted,
          likesCount: Math.max(0, newCount),
          commentsCount: state.commentsCount,
          repostsCount: state.repostsCount,
        }
        localStorage.setItem(
          `post_interactions_${postId}`,
          JSON.stringify(cacheData)
        )
      } else {
        setState(prev => ({
          ...prev,
          liked: !newLiked,
          likesCount: state.likesCount,
        }))
        showToast('Like failed — try again')
      }
    } catch (error) {
      console.error('Like toggle failed:', error)
      setState(prev => ({
        ...prev,
        liked: !newLiked,
        likesCount: state.likesCount,
      }))
      showToast('Like failed — try again')
    } finally {
      setState(prev => ({
        ...prev,
        loading: { ...prev.loading, like: false },
      }))
    }
  }, [postId, state.liked, state.likesCount, state.loading.like, showToast])

  // Save/Unsave functionality
  const toggleSave = useCallback(async () => {
    if (!postId || state.loading.save) return

    setState(prev => ({
      ...prev,
      loading: { ...prev.loading, save: true },
    }))

    const newSaved = !state.saved

    // Optimistic update
    setState(prev => ({ ...prev, saved: newSaved }))

    try {
      const response = newSaved
        ? await api.bookmarkPost(postId)
        : await api.unbookmarkPost(postId)

      if (response.success) {
        const cacheData = {
          liked: state.liked,
          saved: newSaved,
          reposted: state.reposted,
          likesCount: state.likesCount,
          commentsCount: state.commentsCount,
          repostsCount: state.repostsCount,
        }
        localStorage.setItem(
          `post_interactions_${postId}`,
          JSON.stringify(cacheData)
        )
        showToast(newSaved ? 'Added to Bookmarks' : 'Removed from Bookmarks')
      } else {
        setState(prev => ({ ...prev, saved: !newSaved }))
        showToast('Bookmark failed — try again')
      }
    } catch (error) {
      console.error('Save toggle failed:', error)
      setState(prev => ({ ...prev, saved: !newSaved }))
      showToast('Bookmark failed — try again')
    } finally {
      setState(prev => ({
        ...prev,
        loading: { ...prev.loading, save: false },
      }))
    }
  }, [postId, state.saved, state.loading.save, showToast])

  // Repost functionality
  const toggleRepost = useCallback(async () => {
    if (!postId || state.loading.repost) return

    setState(prev => ({
      ...prev,
      loading: { ...prev.loading, repost: true },
    }))

    const newReposted = !state.reposted
    const newCount = newReposted ? state.repostsCount + 1 : state.repostsCount - 1

    // Optimistic update
    setState(prev => ({
      ...prev,
      reposted: newReposted,
      repostsCount: Math.max(0, newCount),
    }))

    try {
      const response = newReposted
        ? await api.repostPost(postId)
        : await api.unrepost(postId)

      if (response.success) {
        // Update local repost cache
        try {
          const reposts = new Set(JSON.parse(localStorage.getItem('reposted_posts') || '[]'))
          if (newReposted) reposts.add(postId); else reposts.delete(postId)
          localStorage.setItem('reposted_posts', JSON.stringify([...reposts]))
        } catch(e) {}
        showToast(newReposted ? 'Reposted' : 'Repost removed')
      } else {
        setState(prev => ({
          ...prev,
          reposted: !newReposted,
          repostsCount: state.repostsCount,
        }))
        showToast('Repost failed — try again')
      }
    } catch (error) {
      console.error('Repost toggle failed:', error)
      setState(prev => ({
        ...prev,
        reposted: !newReposted,
        repostsCount: state.repostsCount,
      }))
      showToast('Repost failed — try again')
    } finally {
      setState(prev => ({
        ...prev,
        loading: { ...prev.loading, repost: false },
      }))
    }
  }, [postId, state.reposted, state.repostsCount, state.loading.repost, showToast])

  // Share functionality with tracking
  const sharePost = useCallback(async (platform?: string) => {
    if (!postId) return false

    try {
      // Use Web Share API if available
      if (navigator.share) {
        await navigator.share({
          title: 'Check out this post on OnRecord',
          text: 'Shared from on record app',
          url: `${window.location.origin}/post/${postId}`,
        })

        // Track share
        await api.trackShare(postId, { platform: platform || 'native', shareType: 'direct' })
        return true
      } else {
        // Fallback: Copy link to clipboard
        const url = `${window.location.origin}/post/${postId}`
        await navigator.clipboard.writeText(url)

        // Track share
        await api.trackShare(postId, { platform: platform || 'clipboard', shareType: 'direct' })
        return true
      }
    } catch (error) {
      console.error('Share failed:', error)
      return false
    }
  }, [postId])

  return {
    ...state,
    actions: {
      toggleLike,
      toggleSave,
      toggleRepost,
      sharePost,
      refresh: loadPostInteractionState,
    },
  }
}

export default useSocialInteractions
